import React, { useState, useEffect } from 'react';
import { Building, Floor, AreaType } from '../types/building';
import { Settings, Plus, Save, X, Home, Box, Trash2 } from 'lucide-react';
import { formatApartmentNumber } from '../utils/formatting';
import { getApartmentLabel } from '../utils/getApartmentLabel';
import { getFloorLabel } from '../utils/floorLabels';

interface BuildingEditorProps {
  building: Building;
  onSave: (building: Building) => void;
  onClose: () => void;
}

export const BuildingEditor: React.FC<BuildingEditorProps> = ({
  building,
  onSave,
  onClose,
}) => {
  const [editedBuilding, setEditedBuilding] = useState<Building>({ ...building });
  const [editedName, setEditedName] = useState(building.name || '');
  const [editedAddress, setEditedAddress] = useState(building.address || '');

  // Update local state when building changes
  useEffect(() => {
    setEditedName(building.name || '');
    setEditedAddress(building.address || '');
    setEditedBuilding({ ...building });
  }, [building]);

  const addFloor = (isBasement: boolean = false) => {
    let floorNumber;
    if (isBasement) {
      // Find the lowest floor number (should be -1 if no basement exists)
      const lowestFloor = Math.min(...editedBuilding.floors.map(f => f.number), 0);
      floorNumber = lowestFloor > -1 ? -1 : lowestFloor - 1;
    } else {
      // For regular floors, continue with the highest number + 1
      floorNumber = editedBuilding.floors.length === 0 ? 0 : Math.max(...editedBuilding.floors.map(f => f.number)) + 1;
    }

    const newFloor: Floor = {
      id: crypto.randomUUID(),
      number: floorNumber,
      apartments: [],
    };

    // Wichtig: Funktionsform des State-Updates verwenden
    setEditedBuilding(prevState => ({
      ...prevState,
      floors: [...prevState.floors, newFloor],
    }));
    
    console.log(`Floor added: ${floorNumber}, ID: ${newFloor.id}`);
  };

  const deleteFloor = (floorId: string) => {
    if (window.confirm('Möchten Sie diese Etage wirklich löschen?')) {
      setEditedBuilding(prevState => ({
        ...prevState,
        floors: prevState.floors.filter(floor => floor.id !== floorId),
      }));
    }
  };

  const addApartment = (floorId: string, areaType?: AreaType) => {
    setEditedBuilding(prevState => {
      // Finde den Floor
      const floorIndex = prevState.floors.findIndex(f => f.id === floorId);
      if (floorIndex === -1) return prevState;
      
      const floor = prevState.floors[floorIndex];
      const newAptNumber = floor.apartments.length + 1;
      const formattedNumber = formatApartmentNumber(floor.number, newAptNumber);
      
      console.log(`Adding apartment: Floor ${floor.number}, Number ${newAptNumber}, Formatted: ${formattedNumber}, Floor ID: ${floorId}`);
      
      // Für Untergeschosse, verwende den angegebenen Bereichstyp oder Standard
      const apartmentAreaType = floor.number < 0 
        ? (areaType || 'residential') 
        : 'residential';
      
      // Erstelle eine neue Wohnung
      const newApartment = {
        id: crypto.randomUUID(),
        number: newAptNumber,
        formattedNumber: formattedNumber,
        status: 'pending',
        notes: '',
        areaType: apartmentAreaType,
      };
      
      // Erstelle eine neue Floors-Array mit der aktualisierten Etage
      const updatedFloors = [...prevState.floors];
      updatedFloors[floorIndex] = {
        ...floor,
        apartments: [...floor.apartments, newApartment],
      };
      
      // Gib das aktualisierte Gebäude zurück
      return {
        ...prevState,
        floors: updatedFloors,
      };
    });
  };

  const deleteApartment = (floorId: string, apartmentId: string) => {
    if (window.confirm('Möchten Sie diese Wohnung wirklich löschen?')) {
      setEditedBuilding(prevState => {
        // Finde den Floor
        const floorIndex = prevState.floors.findIndex(f => f.id === floorId);
        if (floorIndex === -1) return prevState;
        
        const floor = prevState.floors[floorIndex];
        
        // Erstelle eine neue Floors-Array mit der aktualisierten Etage
        const updatedFloors = [...prevState.floors];
        updatedFloors[floorIndex] = {
          ...floor,
          apartments: floor.apartments.filter(apt => apt.id !== apartmentId),
        };
        
        // Gib das aktualisierte Gebäude zurück
        return {
          ...prevState,
          floors: updatedFloors,
        };
      });
    }
  };

  const handleSave = () => {
    if (!editedName.trim() || !editedAddress.trim()) {
      alert('Bitte geben Sie einen Namen und eine Adresse für das Gebäude ein.');
      return;
    }
    
    // Ensure all apartments have a formatted number
    const validatedFloors = editedBuilding.floors.map(floor => ({
      ...floor,
      apartments: floor.apartments.map(apt => ({
        ...apt,
        formattedNumber: apt.formattedNumber || formatApartmentNumber(floor.number, apt.number)
      }))
    }));
    
    // Create the final building object with all data
    const finalBuilding = {
      ...editedBuilding,
      name: editedName,
      address: editedAddress,
      floors: validatedFloors
    };
    
    console.log("Saving building with floors:", finalBuilding);
    onSave(finalBuilding);
  };

  const getAddFloorLabel = () => {
    return editedBuilding.floors.length === 0 ? 'Erdgeschoss hinzufügen' : 'Obergeschoss hinzufügen';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2 break-words">
            <Settings className="w-6 h-6 shrink-0" />
            <span>Gebäudestruktur bearbeiten</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors shrink-0"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800 break-words">Gebäudeinformationen</h3>
            <div>
              <label htmlFor="buildingName" className="block text-sm font-medium text-gray-700 mb-1">
                Gebäudename
              </label>
              <input
                id="buildingName"
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Gebäudename eingeben"
              />
            </div>
            <div>
              <label htmlFor="buildingAddress" className="block text-sm font-medium text-gray-700 mb-1">
                Adresse
              </label>
              <input
                id="buildingAddress"
                type="text"
                value={editedAddress}
                onChange={(e) => setEditedAddress(e.target.value)}
                className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Straße, Hausnummer, PLZ, Ort"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => addFloor(false)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 shrink-0" />
              <span>{getAddFloorLabel()}</span>
            </button>
            <button
              onClick={() => addFloor(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              <Plus className="w-4 h-4 shrink-0" />
              <span>Untergeschoss hinzufügen</span>
            </button>
          </div>

          {editedBuilding.floors.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-500">Fügen Sie ein Geschoss hinzu, um zu beginnen</p>
            </div>
          ) : (
            editedBuilding.floors
              .sort((a, b) => b.number - a.number)
              .map(floor => (
                <div key={floor.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                      <h4 className="text-lg font-bold text-gray-800 break-words">{getFloorLabel(floor)}</h4>
                      <button
                        onClick={() => deleteFloor(floor.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors shrink-0"
                        title="Etage löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {floor.number < 0 ? (
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => addApartment(floor.id, 'residential')}
                          className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          title="Wohnbereich hinzufügen"
                        >
                          <Home className="w-4 h-4 shrink-0" />
                          <Plus className="w-4 h-4 shrink-0" />
                          <span className="hidden sm:inline">Wohnbereich</span>
                        </button>
                        <button
                          onClick={() => addApartment(floor.id, 'cellar')}
                          className="flex items-center gap-1 px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                          title="Kellerbereich hinzufügen"
                        >
                          <Box className="w-4 h-4 shrink-0" />
                          <Plus className="w-4 h-4 shrink-0" />
                          <span className="hidden sm:inline">Kellerbereich</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => addApartment(floor.id)}
                        className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 shrink-0" />
                        <span>Wohnung hinzufügen</span>
                      </button>
                    )}
                  </div>

                  {floor.apartments.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Keine Wohnungen vorhanden</p>
                  ) : floor.apartments.length === 2 ? (
                    // Spezialfall für genau 2 Wohnungen: Grid mit 2 Spalten
                    <div className="grid grid-cols-2 gap-4">
                      {floor.apartments.map(apt => (
                        <div key={apt.id} className="col-span-1">
                          <div className="p-3 bg-white rounded-md shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                {apt.areaType === 'cellar' ? (
                                  <Box className="w-4 h-4 text-gray-600 shrink-0" />
                                ) : (
                                  <Home className="w-4 h-4 text-gray-600 shrink-0" />
                                )}
                                <span className="text-lg font-bold truncate">
                                  {getApartmentLabel(apt)} {apt.formattedNumber}
                                </span>
                              </div>
                              
                              <button
                                onClick={() => deleteApartment(floor.id, apt.id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded-full shrink-0"
                                title="Wohnung löschen"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Standard-Grid für 1 oder 3+ Wohnungen
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {floor.apartments.map(apt => (
                        <div
                          key={apt.id}
                          className={`p-3 bg-white rounded-md shadow-sm hover:shadow-md transition-shadow ${floor.apartments.length === 1 ? 'sm:col-span-2' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              {apt.areaType === 'cellar' ? (
                                <Box className="w-4 h-4 text-gray-600 shrink-0" />
                              ) : (
                                <Home className="w-4 h-4 text-gray-600 shrink-0" />
                              )}
                              <span className="text-lg font-bold truncate">
                                {getApartmentLabel(apt)} {apt.formattedNumber}
                              </span>
                            </div>
                            
                            <button
                              onClick={() => deleteApartment(floor.id, apt.id)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded-full shrink-0"
                              title="Wohnung löschen"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Save className="w-4 h-4 shrink-0" />
            <span>Speichern</span>
          </button>
        </div>
      </div>
    </div>
  );
};